import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { company, nav, buildQuoteUrl } from "@/config/site";
import { categories, activeProducts } from "@/data/products";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  const allProducts = activeProducts();

  return (
    <header
      className="sticky top-0 z-50 w-full bg-white border-b border-neutral-200"
      data-testid="site-navbar"
    >
      <div className="border-b border-neutral-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs text-neutral-500">
          <span className="tracking-wide">
            Greater Noida, India · Established {company.establishedYear}
          </span>
          <div className="flex items-center gap-4">
            {company.email ? (
              <a href={`mailto:${company.email}`} className="hover:text-[#A91D22]" data-testid="topbar-email-link">
                {company.email}
              </a>
            ) : null}
            <a href={company.phoneHref} className="hover:text-[#A91D22] inline-flex items-center gap-1.5" data-testid="topbar-phone-link">
              <Phone size={13} aria-hidden="true" />
              {company.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 lg:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link" aria-label={company.brandName}>
            <img src={company.logo.src} alt={company.logo.alt} className="h-8 md:h-10 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {nav.map((item) => {
              if (item.label === "Products") {
                return (
                  <div
                    key={item.to}
                    className="relative"
                    onMouseEnter={() => setProductsOpen(true)}
                    onMouseLeave={() => setProductsOpen(false)}
                  >
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `px-3 py-2 text-sm font-medium inline-flex items-center gap-1 transition-colors ${isActive ? "text-[#A91D22]" : "text-neutral-700 hover:text-[#A91D22]"}`
                      }
                      data-testid="nav-products"
                    >
                      Products
                      <ChevronDown size={14} aria-hidden="true" />
                    </NavLink>
                    {productsOpen && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[780px]"
                        data-testid="products-megamenu"
                      >
                        <div className="bg-white border border-neutral-200 shadow-lg p-6 grid grid-cols-2 gap-6">
                          {categories.map((cat) => (
                            <div key={cat.id}>
                              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A91D22] mb-3">
                                {cat.name}
                              </div>
                              <ul className="space-y-1.5">
                                {cat.slugs.map((slug) => {
                                  const p = allProducts.find((x) => x.slug === slug);
                                  if (!p) return null;
                                  return (
                                    <li key={slug}>
                                      <Link
                                        to={`/products/${slug}`}
                                        className="text-sm text-neutral-700 hover:text-[#A91D22]"
                                        data-testid={`megamenu-link-${slug}`}
                                      >
                                        {p.name}
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#A91D22]" : "text-neutral-700 hover:text-[#A91D22]"}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:block">
            <a
              href={buildQuoteUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-[#A91D22] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#8B181C] transition-colors rounded-sm"
              data-testid="nav-cta-quote"
            >
              Request a Quote
            </a>
          </div>

          <button
            className="lg:hidden p-2 -mr-2 text-neutral-900"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `block px-3 py-3 text-sm font-medium border-b border-neutral-100 ${isActive ? "text-[#A91D22]" : "text-neutral-800"}`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={buildQuoteUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex w-full items-center justify-center bg-[#A91D22] text-white text-sm font-medium px-5 py-3 hover:bg-[#8B181C] rounded-sm"
              data-testid="mobile-cta-quote"
            >
              Request a Quote
            </a>
            <a
              href={company.phoneHref}
              className="mt-2 inline-flex w-full items-center justify-center border border-neutral-300 text-neutral-900 text-sm font-medium px-5 py-3 rounded-sm"
            >
              <Phone size={14} className="mr-2" aria-hidden="true" />
              {company.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
